// İSG uzmanı portalı — izin aksiyon kaydı (denetim izi) satırının PAYLAŞILAN gövdesi (owner
// review B3-3, api-contract §35.1). İki tüketici AYNI bileşeni kullanır: `PermitActivityPanel.tsx`
// (izin detayı "Aksiyonlar" tabı, açan/reviewer içindir) VE `DepartmentReviewPanels.tsx`'in
// `ReviewActivitiesPanel`'i (Kontrol aşaması salt-okunur "AI notları + Aksiyonlar" kartı —
// api-contract §35.1 "§31.5 departman detayı AYNI DTO'yu ve aynı listeyi taşır"). İkisi eskiden
// (S33) birbirinden BAĞIMSIZ, neredeyse birebir aynı `ActivityRow`/`ChangeRow` kopyalıyordu —
// S35'in yeni alanlarını (event/actorType/fromStatus/toStatus/reason/departments/subject) İKİ
// yerde AYRI AYRI kurmak, ikisinin sessizce AYRIŞMASI riskini taşırdı (bu dosyanın var oluş
// sebebi tam olarak bu). Yalnız DIŞ kabuk (kutulu kart vs. saç-çizgisi liste) çağıranda kalır.
//
// **Metin güvenliği.** `activity.text`/`reason` kullanıcı/AI türevli SERBEST METİNdir — ikisi de
// düz JSX ifadesi olarak render edilir (`{value}`), `dangerouslySetInnerHTML` KULLANILMAZ.

import {
  Tag,
  type WorkPermitActivityChange,
  type WorkPermitActivityDto,
  type WorkPermitActivityKind,
  cn,
  currentCulture,
} from '@isgportal/shared';
import { ArrowRight, MessageSquare, Settings2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../lib/format';
import {
  getPermitStatusPresentation,
  permitActivityActorRoleKey,
  permitActivityTitle,
} from './permitEnums';

/** Sistem-log (dişli, nötr) veya yorum (balon, marka) ikonu — kaydın `kind`ine göre. */
export function ActivityAvatar({ kind }: { kind: WorkPermitActivityKind }) {
  const isComment = kind === 'Comment';
  return (
    <div
      className={cn(
        'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
        isComment ? 'bg-primary/10 text-primary' : 'bg-surface-muted text-muted',
      )}
      aria-hidden="true"
    >
      {isComment ? <MessageSquare className="h-4 w-4" /> : <Settings2 className="h-4 w-4" />}
    </div>
  );
}

/** Bir alan değişikliği: "Alan: eski → yeni" (boş değer tire). Alan adı i18n ile eşlenir. */
function ChangeRow({ change }: { change: WorkPermitActivityChange }) {
  const { t } = useTranslation();
  const fieldLabel = t(`permits.field.${change.field}`, { defaultValue: change.field });
  const oldValue = change.oldValue && change.oldValue.length > 0 ? change.oldValue : '—';
  const newValue = change.newValue && change.newValue.length > 0 ? change.newValue : '—';

  return (
    <li className="flex flex-wrap items-center gap-1.5 text-xs text-muted">
      <span className="font-medium text-fg">{fieldLabel}:</span>
      <span className="rounded bg-surface-muted px-1.5 py-0.5 line-through">{oldValue}</span>
      <ArrowRight className="h-3 w-3" aria-hidden="true" />
      <span className="rounded bg-success-subtle px-1.5 py-0.5 text-success">{newValue}</span>
    </li>
  );
}

/** İki durum arasındaki geçiş — `getPermitStatusPresentation`in TEK renk/etiket kaynağından. */
function StatusTransition({
  fromStatus,
  toStatus,
}: {
  fromStatus: WorkPermitActivityDto['fromStatus'];
  toStatus: WorkPermitActivityDto['toStatus'];
}) {
  const { t } = useTranslation();
  if (!fromStatus && !toStatus) {
    return null;
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {fromStatus ? (
        <>
          <Tag color={getPermitStatusPresentation(fromStatus).tagColor}>
            {t(getPermitStatusPresentation(fromStatus).labelKey)}
          </Tag>
          <ArrowRight className="h-3 w-3 text-muted" aria-hidden="true" />
        </>
      ) : null}
      {toStatus ? (
        <Tag color={getPermitStatusPresentation(toStatus).tagColor}>
          {t(getPermitStatusPresentation(toStatus).labelKey)}
        </Tag>
      ) : null}
    </div>
  );
}

/** Departman rozetleri (§31 `PermitApprovalDepartment`) — `DepartmentEscalated` "kimden → kime"
 * iki elemanlı diziyi ok ile, diğerleri (gerekli küme / tekil onaylayan-reddeden) yan yana listeler. */
function DepartmentChips({
  departments,
  isEscalation,
}: {
  departments: NonNullable<WorkPermitActivityDto['departments']>;
  isEscalation: boolean;
}) {
  const { t } = useTranslation();
  if (isEscalation && departments.length === 2) {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <Tag color="blue">{t(`permits.department.${departments[0]}`)}</Tag>
        <ArrowRight className="h-3 w-3 text-muted" aria-hidden="true" />
        <Tag color="blue">{t(`permits.department.${departments[1]}`)}</Tag>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {departments.map((d) => (
        <Tag key={d} color="blue">
          {t(`permits.department.${d}`)}
        </Tag>
      ))}
    </div>
  );
}

/** Kayıt gövdesi: aktör + zaman damgası, başlık/yorum metni, durum geçişi, departmanlar,
 * gerekçe, hedef kişi ve alan değişiklik listesi. `className` çağıranın kendi kart/liste
 * çerçevesini (dış kabuk) eklemesi içindir. */
export function ActivityBody({
  activity,
  className,
}: {
  activity: WorkPermitActivityDto;
  className?: string;
}) {
  const { t } = useTranslation();
  const culture = currentCulture();
  const isComment = activity.kind === 'Comment';

  const authorLabel =
    activity.authorName ??
    (activity.actorType === 'Ai'
      ? t('permits.activities.aiAuthor')
      : t('permits.activities.systemAuthor'));
  const roleKey = permitActivityActorRoleKey(activity);
  const title = permitActivityTitle(activity, t);

  return (
    <div className={cn('flex flex-1 flex-col gap-1', className)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium text-fg">
          {authorLabel}
          {roleKey ? <span className="ml-1 font-normal text-muted">· {t(roleKey)}</span> : null}
        </span>
        <span className="text-xs text-muted">{formatDateTime(activity.at, culture)}</span>
      </div>
      <span className="sr-only">
        {isComment ? t('permits.activities.comment') : t('permits.activities.systemLog')}
      </span>

      {/* Serbest metin — kullanıcı/AI türevli; DÜZ metin (dangerouslySetInnerHTML YOK). */}
      {title ? (
        <p className="text-sm text-fg">{title}</p>
      ) : activity.text ? (
        <p className="text-sm text-fg">{activity.text}</p>
      ) : null}

      <StatusTransition fromStatus={activity.fromStatus} toStatus={activity.toStatus} />

      {activity.departments && activity.departments.length > 0 ? (
        <DepartmentChips
          departments={activity.departments}
          isEscalation={activity.event === 'DepartmentEscalated'}
        />
      ) : null}

      {activity.reason ? (
        <p className="text-sm text-fg">
          {t('permits.activities.reasonLabel', { reason: activity.reason })}
        </p>
      ) : null}

      {activity.subject ? (
        <p className="text-xs text-muted">
          {t('permits.activities.subjectLabel', { name: activity.subject.name })}
        </p>
      ) : null}

      {activity.changeSet && activity.changeSet.length > 0 ? (
        <ul className="mt-1 flex flex-col gap-1">
          {activity.changeSet.map((change, i) => (
            // Değişiklik satırları backend sırasını korur; alan adı + değer çifti sabit olduğundan
            // index anahtar güvenlidir (liste yeniden sıralanmaz).
            // biome-ignore lint/suspicious/noArrayIndexKey: değişmez, yeniden sıralanmayan snapshot satırları
            <ChangeRow key={i} change={change} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}
